// Layout components

import { Server } from '@healthblocks-io/core'
import { createBrowserRouter, Outlet } from 'react-router-dom'

import { MediaQuery } from './@bothrs-ui/web/components/media-query/MediaQuery'
import { AppRoot } from './AppRoot'
import { breakpoints } from './config/layout'
import { PageArea } from './layout/components/page-area/PageArea'
import { PageLayout } from './layout/components/page-layout'
import { AppUpdateRoutes } from './modules/app/pages/app-update/app-update.routes'
import { BlockingAdminMessagePage } from './modules/app/pages/blocking-admin-message/BlockingAdminMessage.page'
import { ErrorPage } from './modules/app/pages/error/Error.page'
import { RequiredVersion } from './modules/app/pages/version/RequiredVersion.page'
import { appointmentRoutes } from './modules/appointment/appointment.routes'
import { authRoutes } from './modules/auth/auth.routes'
import { Authenticated } from './modules/auth/components/authenticated/Authenticated'
import { LandingPage } from './modules/auth/pages/landing/Landing.page'
import { biometricRoutes } from './modules/biometrics/biometric.routes'
import { careRoutes } from './modules/care/Care.routes'
import { careQuestionRoutes } from './modules/care-question/care-question.routes'
import { contentRoutes } from './modules/content/Content.routes'
import { ContentPreviewPage } from './modules/content/pages/preview/ContentPreview.page'
import { ProgramPreviewPage } from './modules/content/pages/preview/ProgramPreview.page'
import { documentsRoutes } from './modules/documents/documents.routes'
import { signableDocumentsRoutes } from './modules/documents/signable-documents.routes'
import { servers, tenantSlugs } from './modules/healthblocks/ServerProvider'
import { homeRoutes } from './modules/home/Home.routes'
import { hylyghtRoutes } from './modules/hylyght/hylyght.routes'
import { messageRoutes } from './modules/messages/Message.routes'
import { notificationsRoutes } from './modules/notifications/Notifications.routes'
import { onboardingRoutes } from './modules/onboarding/onboarding.routes'
import { paymentRoutes } from './modules/payment/payment.routes'
import { profileRoutes } from './modules/profile/Profile.routes'
import { AwellQuestionnaireCompletion } from './modules/questionnaire/pages/awell-questionnaire-completed/AwellQuestionnaireCompleted.page'
import { IntramurosQuestionnairePage } from './modules/questionnaire/pages/intramuros/IntramurosQuestionnaire.page'
import { questionnaireRoutes } from './modules/questionnaire/Questionnaire.routes'
import { BackCompatRedirect } from './modules/shared/components/back-compat-redirect/BackCompatRedirect'
import { SynopsGuard } from './modules/shared/components/synops-guard/SynopsGuard'
import { TenantGuard } from './modules/shared/components/tenant-guard/TenantGuard'
import { WebHeader } from './modules/shared/components/web-header/WebHeader'
import { IntramurosHeader } from './modules/task/components/intramuros-header/IntramurosHeader'
import { IntramurosTaskDetailPage } from './modules/task/pages/IntramurosTasksDetail.page'
import { TaskRootPage } from './modules/task/pages/TaskRoot.page'
import { taskRoutes } from './modules/task/Task.routes'
import { IntramurosAuthenticationProvider } from './modules/timeline/context/IntramurosProvider'
import { timelineRoutes } from './modules/timeline/Timeline.routes'
import { RootNavigation } from './RootNavigation'

const ErrorBoundaryWithServer = () => {
  const tenant = tenantSlugs.find((slug) => window.location.pathname.startsWith(`/${slug}`))
  const server = servers.find((s) => s.tenant === tenant) ?? servers[0]

  if (!server) return <ErrorPage />

  return (
    <Server.Provider value={server}>
      <ErrorPage />
    </Server.Provider>
  )
}

export const router = createBrowserRouter([
  {
    path: '/awell-questionnaire-completion',
    element: <AwellQuestionnaireCompletion />,
  },
  {
    path: '/',
    element: <AppRoot />,
    children: [
      // Universal routes (landing, login, etc.)
      { index: true, element: <LandingPage /> },
      { path: '/select-hospital', element: <LandingPage /> },
      {
        path: '/error',
        element: <ErrorPage />,
      },

      // Tenant-scoped routes (both public and private)
      {
        path: '/:tenant',
        element: <TenantGuard />,
        children: [
          // Intramuros routes (no authentication required)
          {
            path: 'intramuros/:key',
            element: <IntramurosAuthenticationProvider />,
            children: [
              {
                element: (
                  <PageLayout
                    header={
                      <PageArea gridArea='header'>
                        <IntramurosHeader />
                      </PageArea>
                    }
                  />
                ),
                children: [
                  {
                    element: <TaskRootPage />,
                    children: [
                      { index: true, element: null },
                      {
                        path: 'detail/:id',
                        element: <IntramurosTaskDetailPage />,
                      },
                    ],
                  },
                  {
                    path: 'questionnaire/:slug',
                    element: <IntramurosQuestionnairePage />,
                  },
                ],
              },
            ],
          },

          // Blocking admin message has absolute priority — no version or Synops guards
          {
            path: 'blocking-admin-message',
            element: <BlockingAdminMessagePage />,
          },

          // All other routes require a supported app version
          {
            element: (
              <RequiredVersion>
                <Outlet />
              </RequiredVersion>
            ),
            children: [
              // Dummy route for link actions - required for desktop. UniversalLinkManager handles redirects
              {
                path: 'link/:action',
                element: <div>link</div>,
              },
              {
                path: 'content-preview/:id',
                element: <ContentPreviewPage />,
              },
              {
                path: 'program-preview/:id',
                element: <ProgramPreviewPage />,
              },
              // Auth routes
              ...authRoutes,
              // Payment routes
              ...paymentRoutes,

              // Routes that require Synops connectivity
              {
                element: <SynopsGuard />,
                children: [
                  // Tenant-scoped private routes (require authentication)
                  {
                    element: (
                      <Authenticated>
                        <Outlet />
                      </Authenticated>
                    ),
                    children: [
                      // Routes with a webheader enabled
                      {
                        element: (
                          <PageLayout
                            header={
                              <MediaQuery query={breakpoints.fromS}>
                                <PageArea gridArea='header'>
                                  <WebHeader />
                                </PageArea>
                              </MediaQuery>
                            }
                            navigation={<RootNavigation />}
                          />
                        ),
                        children: [
                          ...AppUpdateRoutes,
                          ...careRoutes,
                          ...contentRoutes,
                          ...documentsRoutes,
                          ...homeRoutes,
                          ...messageRoutes,
                          ...profileRoutes,
                          ...taskRoutes,
                          ...timelineRoutes,
                        ],
                      },
                      // Routes without a webheader enabled
                      {
                        element: <PageLayout navigation={<RootNavigation />} />,
                        children: [
                          ...appointmentRoutes,
                          ...questionnaireRoutes,
                          ...notificationsRoutes,
                          ...signableDocumentsRoutes,
                          ...hylyghtRoutes,
                          ...careQuestionRoutes,
                        ],
                      },
                      // Routes without navigation
                      {
                        element: <Outlet />,
                        children: [...onboardingRoutes, ...biometricRoutes],
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },

      // Back-compat redirect for legacy unprefixed URLs
      {
        path: '*',
        element: <BackCompatRedirect />,
      },
    ],
    errorElement: <ErrorBoundaryWithServer />,
  },
])
